<template>
  <SmDrawer :visibility="visible" @update:visibility="handleVisibilityChange">
    <template #title>
      <div class="flex items-center gap-3">
        <div
          class="from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br">
          <AppWindow class="text-blue-600 dark:text-blue-400 h-5 w-5" />
        </div>
        <h1 class="font-semibold text-gray-900 dark:text-white">
          {{ softwareName }}
        </h1>
      </div>
    </template>

    <!-- Deployment Detail Drawer (nested) -->
    <DeploymentDetailDrawer
      v-model="deploymentDrawerVisible"
      :deployment-id="selectedDeploymentIdForDrawer"
      :deployment-name="selectedDeploymentNameForDrawer" />

    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col gap-6">
      <!-- KPI Skeletons -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <el-card shadow="never">
          <SmSkeleton height="80px" width="100%" />
        </el-card>
        <el-card shadow="never">
          <SmSkeleton height="80px" width="100%" />
        </el-card>
        <el-card shadow="never">
          <SmSkeleton height="80px" width="100%" />
        </el-card>
      </div>

      <!-- Chart Skeleton -->
      <div
        class="flex-shrink-0 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <SmSkeleton height="20px" width="30%" class="mb-4" />
        <SmSkeleton height="250px" width="100%" />
      </div>

      <!-- Deployments Skeleton -->
      <div>
        <SmSkeleton height="20px" width="40%" class="mb-4" />
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <el-card v-for="i in 3" :key="i" shadow="never">
            <SmSkeleton height="100px" width="100%" />
          </el-card>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!stats"
      class="flex flex-col items-center justify-center gap-4 py-12">
      <div
        class="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
        <BarChart2 class="h-8 w-8 text-gray-400" />
      </div>
      <p class="text-center text-gray-500 dark:text-gray-400">
        {{ i18n.t('general.noData') }}
      </p>
      <button
        type="button"
        class="rounded-md bg-blue-600 hover:bg-blue-700 px-4 py-2 text-sm font-medium text-white transition-colors"
        @click="refetch">
        {{ i18n.t('browserExtension.retry') }}
      </button>
    </div>

    <!-- Loaded State -->
    <div v-else class="flex flex-col gap-6">
      <!-- Deployment Filter Selector -->
      <div class="w-64">
        <SmSelect
          :selection="selectedDeploymentOption"
          :options="deploymentOptions"
          :label="i18n.t('browserExtension.allDeployments')"
          :searchable="false"
          @change="handleDeploymentSelect" />
      </div>

      <!-- Usage Summary (KPIs + Heatmap) -->
      <UsageSummary
        :stats="stats"
        variant="tool"
        :title="i18n.t('browserExtension.dailyUsage')"
        :subtitle="selectedDeploymentName" />

      <!-- Deployments Section -->
      <div>
        <div class="mb-4 flex items-center justify-between">
          <h3 class="font-semibold text-gray-900 dark:text-white">
            {{ i18n.t('browserExtension.deploymentBreakdown') }}
          </h3>
        </div>

        <!-- Empty Deployments -->
        <div
          v-if="filteredDeploymentsData.length === 0"
          class="rounded-lg border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <Server class="mx-auto h-12 w-12 text-gray-400" />
          <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {{ i18n.t('browserExtension.noDeploymentData') }}
          </p>
        </div>

        <!-- Deployments Cards -->
        <div v-else class="flex flex-col gap-3">
          <DeploymentCard
            v-for="deployment in filteredDeploymentsData"
            :key="
              deployment._id ||
              deployment.deployment_id ||
              deployment.deployment_name
            "
            :deployment="deployment"
            @click="handleDeploymentClick" />
        </div>
      </div>
    </div>
  </SmDrawer>
</template>

<script setup lang="ts">
  import { computed, ref, watch, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Server, AppWindow, BarChart2 } from 'lucide-vue-next'
  import SmDrawer from '@/components/sm/SmDrawer.vue'
  import SmSelect, { type Option } from '@/components/sm/SmSelect.vue'
  import DeploymentCard from '../dashboard/widgets/DeploymentCard.vue'
  import DeploymentDetailDrawer from './DeploymentDetailDrawer.vue'
  import UsageSummary from '../dashboard/widgets/UsageSummary.vue'
  import SmSkeleton from '@/components/sm/SmSkeleton.vue'
  import {
    useBrowserLastUsageBySoftware,
    type BrowserLastUsageBySoftware,
    getToolUsageStatsByDeployment,
  } from '@/stores/browserextension/browserextension.store'
  import { BrowserLastUsageBySoftwareFilters } from '@/stores/browserextension/browserextension.filter'
  import { useReactiveQuery } from '@/composables/useReactiveQuery'

  const props = defineProps<{
    modelValue: boolean
    softwareName: string | null
    initialDeploymentId?: string | null
  }>()

  const emit = defineEmits(['update:modelValue'])

  const i18n = useI18n()
  const selectedDeploymentId = ref<string | null>(
    props.initialDeploymentId || null
  )

  // Sync selectedDeploymentId with prop when it changes
  watch(
    () => props.initialDeploymentId,
    (newId) => {
      selectedDeploymentId.value = newId || null
    }
  )

  // 1. Fetch Deployments Data (for filtering)
  const filter = computed<BrowserLastUsageBySoftwareFilters>(() => ({
    search: props.softwareName || '',
  }))
  const { data: deploymentsData, unsubscribe: unsubLastUsage } =
    useBrowserLastUsageBySoftware(filter)

  // Cleanup subscriptions on unmount to prevent memory leaks
  onUnmounted(() => {
    unsubLastUsage()
  })

  const visible = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
  })

  const handleVisibilityChange = (val: boolean) => {
    emit('update:modelValue', val)
  }

  // 2. Filter deployments for current software
  const filteredDeploymentsData = computed<BrowserLastUsageBySoftware[]>(() => {
    if (!deploymentsData.value || !props.softwareName) return []
    return deploymentsData.value.filter(
      (d) => d.software_name === props.softwareName && d.deployment_id
    )
  })

  // 3. Build Deployment Options
  const deploymentOptions = computed<Option<string | null>[]>(() => {
    const options: Option<string | null>[] = [
      {
        label: i18n.t('browserExtension.allDeployments'),
        value: null,
      },
    ]

    if (filteredDeploymentsData.value) {
      filteredDeploymentsData.value.forEach((d) => {
        if (d.deployment_id) {
          options.push({
            label:
              d.deployment_name || i18n.t('browserExtension.unknownDeployment'),
            value: d.deployment_id,
          })
        }
      })
    }
    return options
  })

  const selectedDeploymentOption = computed<Option<string | null>[]>(() => {
    if (selectedDeploymentId.value === null) {
      return []
    }
    const option = deploymentOptions.value.find(
      (o) => o.value === selectedDeploymentId.value
    )
    return option ? [option] : []
  })

  const selectedDeploymentName = computed(() => {
    if (!selectedDeploymentId.value) return null
    const deployment = filteredDeploymentsData.value.find(
      (d) => d.deployment_id === selectedDeploymentId.value
    )
    return (
      deployment?.deployment_name ||
      i18n.t('browserExtension.unknownDeployment')
    )
  })

  // 4. Use Reactive Query for Stats (Prevents race conditions & duplication)
  const fetchParams = computed(() => ({
    softwareName: props.softwareName,
    deploymentId: selectedDeploymentId.value,
    isVisible: props.modelValue,
  }))

  const {
    data: statsData,
    loading,
    refetch,
  } = useReactiveQuery(async (params) => {
    if (!params.softwareName || !params.isVisible) return []

    const result = await getToolUsageStatsByDeployment(
      params.deploymentId,
      params.softwareName,
      null,
      null
    )

    return [result] // Wrap in array to match generic type
  }, fetchParams)

  const stats = computed(() => statsData.value[0] || null)

  function handleDeploymentSelect(
    selection: Option<string | null>[] | undefined
  ) {
    const selectedValue =
      selection && selection.length > 0 ? selection[0].value : null
    selectedDeploymentId.value = selectedValue
    // No need to manually call loadStats, the watcher in useReactiveQuery handles it
  }

  // Deployment Drawer (nested)
  const deploymentDrawerVisible = ref(false)
  const selectedDeploymentIdForDrawer = ref<string | null>(null)
  const selectedDeploymentNameForDrawer = ref<string | undefined>(undefined)

  function handleDeploymentClick(deployment: BrowserLastUsageBySoftware) {
    selectedDeploymentIdForDrawer.value = deployment.deployment_id || null
    selectedDeploymentNameForDrawer.value = deployment.deployment_name
    deploymentDrawerVisible.value = true
  }
</script>
